// Sorting, paging and bulk-update helpers for the Active Deals table. Pure, so
// they can be tested without rendering the page.

export type DealSortKey = 'title' | 'stage' | 'value' | 'probability' | 'owner' | 'closeDate'
export type SortDirection = 'asc' | 'desc'
export interface DealSort {
  key: DealSortKey
  dir: SortDirection
}

export interface SortableDeal {
  title?: string | null
  stage?: string | null
  value?: number | null
  probability?: number | null
  owner?: string | null
  closeDate?: string | null
  pinned?: boolean
}

/** closeDate is free text ("Oct 31, 2026", "Q4 2026"); null when it isn't a real date. */
export function parseCloseDate(closeDate?: string | null): Date | null {
  if (!closeDate) return null
  const d = new Date(closeDate)
  return isNaN(d.getTime()) ? null : d
}

const text = (v?: string | null) => (v ?? '').toLowerCase()

/**
 * Sorts by `sort`, always keeping pinned deals on top (as the cards do). Empty
 * values sort last whichever way you sort, so "no target date" never leads.
 * Without a sort, the original order is kept.
 */
export function sortDeals<T extends SortableDeal>(deals: T[], sort: DealSort | null): T[] {
  const indexed = deals.map((deal, index) => ({ deal, index }))
  const factor = sort?.dir === 'desc' ? -1 : 1

  const compare = (a: T, b: T): number => {
    if (!sort) return 0
    switch (sort.key) {
      case 'title':
      case 'stage':
      case 'owner': {
        const x = text(a[sort.key])
        const y = text(b[sort.key])
        if (!x && !y) return 0
        if (!x) return 1
        if (!y) return -1
        return x.localeCompare(y) * factor
      }
      case 'value':
      case 'probability': {
        const x = a[sort.key]
        const y = b[sort.key]
        const xe = x === null || x === undefined
        const ye = y === null || y === undefined
        if (xe && ye) return 0
        if (xe) return 1
        if (ye) return -1
        return (x - y) * factor
      }
      case 'closeDate': {
        const x = parseCloseDate(a.closeDate)
        const y = parseCloseDate(b.closeDate)
        if (!x && !y) return 0
        if (!x) return 1
        if (!y) return -1
        return (x.getTime() - y.getTime()) * factor
      }
    }
  }

  return indexed
    .sort((p, q) => {
      const pin = Number(Boolean(q.deal.pinned)) - Number(Boolean(p.deal.pinned))
      if (pin !== 0) return pin
      const byKey = compare(p.deal, q.deal)
      return byKey !== 0 ? byKey : p.index - q.index
    })
    .map((x) => x.deal)
}

export const PAGE_SIZES = [10, 25, 50, 100] as const

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize))
}

/** One page of `items`; an out-of-range page is clamped to the nearest real one. */
export function paginate<T>(items: T[], page: number, pageSize: number) {
  const pages = pageCount(items.length, pageSize)
  const current = Math.min(Math.max(1, page), pages)
  const start = (current - 1) * pageSize
  return {
    rows: items.slice(start, start + pageSize),
    page: current,
    pages,
    from: items.length === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, items.length),
    total: items.length,
  }
}

// ---------------------------------------------------------------- bulk updates

export interface BulkResult {
  updated: string[]
  /** The server refused (403/404): not the person's deal to change, or already gone. */
  skipped: string[]
  failed: string[]
}

const statusOf = (err: unknown): number | undefined => (err as any)?.response?.status

/**
 * Runs `worker` for every id, a few at a time, never throwing: a 403/404 counts
 * as skipped (the person can't change that deal), anything else as failed.
 */
export async function runBulk(
  ids: string[],
  worker: (id: string) => Promise<unknown>,
  concurrency = 5
): Promise<BulkResult> {
  const result: BulkResult = { updated: [], skipped: [], failed: [] }
  let next = 0

  const lane = async () => {
    while (next < ids.length) {
      const id = ids[next++]
      try {
        await worker(id)
        result.updated.push(id)
      } catch (err) {
        const status = statusOf(err)
        if (status === 403 || status === 404) result.skipped.push(id)
        else result.failed.push(id)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, ids.length) }, lane))
  return result
}

const deals = (n: number) => `${n} ${n === 1 ? 'deal' : 'deals'}`

/** The one-line toast for a finished bulk update, and whether it counts as a success. */
export function describeBulkResult(result: BulkResult, what: string): { message: string; ok: boolean } {
  const { updated, skipped, failed } = result
  const parts: string[] = []
  if (updated.length > 0) parts.push(`${what}: ${deals(updated.length)} updated`)
  if (skipped.length > 0) parts.push(`${deals(skipped.length)} skipped (you can only change your own deals)`)
  if (failed.length > 0) parts.push(`${deals(failed.length)} failed`)
  if (parts.length === 0) return { message: 'No deals selected.', ok: false }
  return { message: parts.join(' · '), ok: skipped.length === 0 && failed.length === 0 }
}
